/* pdp-fitment-gate.js — deferred-fitment Add to Cart gate
 * Inline customization layout + deferred fitment modal.
 * Gated behind window.__pdpFitmentGate (always on).
 *
 * The base page logic ships the Add to Cart buttons disabled and only enables
 * them after full fitment. This gate normally keeps them clickable (except on a
 * non-fit answer) and gates at the #product-form submit so the modal can collect
 * fitment. A country/manufacturing VIN check is a required ACQ, so its PDP Add
 * to Cart remains disabled until that exact VIN verification succeeds.
 */
(function () {
	"use strict";

  if (window.PartifyPdpFitmentGate) return;
  function createLifecycle() {
    const controller = new AbortController();
    const observers = [];
    const timers = new Set();
    return {
      listen(target, name, listener, options) {
        target.addEventListener(name, listener, { ...(typeof options === 'boolean' ? { capture: options } : options), signal: controller.signal });
      },
      Observer: class extends MutationObserver {
        constructor(callback) { super(callback); observers.push(this); }
      },
      delay(callback, ms) {
        const id = window.setTimeout(() => { timers.delete(id); if (!controller.signal.aborted) callback(); }, ms);
        timers.add(id);
        return id;
      },
      destroy() { controller.abort(); observers.forEach(observer => observer.disconnect()); timers.forEach(id => window.clearTimeout(id)); }
    };
  }
  let activeInstance;
  function mount(options = {}) {
    activeInstance?.destroy();
    const lifecycle = createLifecycle();
    const listen = lifecycle.listen;
    const FitmentObserver = lifecycle.Observer;
    const delay = lifecycle.delay;


	const LOG = function () {
		const args = Array.prototype.slice.call(arguments);
		args.unshift("wool [pdp-fitment-gate]");
		console.log.apply(console, args);
	};

	const ATC_BTN_SELECTOR =
		".add-to-cart, .add-to-cart-for-unpainted, .sticky-add-to-cart";
	const REVIEW_PROPERTY_NAME = "Fitment Check";
	const REVIEW_PROPERTY_VALUE = "Overridden";
	const REVIEW_REASON_PROPERTY_NAME = "Fitment Review Reason";
	const REVIEW_REASON_BODY_TYPE = "VIN/body type mismatch";
	const REVIEW_REASON_COUNTRY = "Manufacturing country mismatch";
	const REVIEW_REASON_EXCLUSION = "Fitment question exclusion";
	const REVIEW_REASON_FITMENT = "Vehicle/part fitment mismatch";
	const SIDE_FITMENT_PROPERTY_NAME = "properties[Position]";

	let modalOpen = false;
	let movedNodes = []; // { node, parent, nextSibling } for exact restoration
	let resolutionContinuation = null;
	let reviewAcknowledged = false;
	let lastFocusedElement = null;

	/* ── Readiness helpers ── */

	function getVehicle() {
		try {
			const a = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return Array.isArray(a) && a.length && a[0] && a[0].model ? a[0] : null;
		} catch (_) {
			return null;
		}
	}

	function getFitmentState() {
		const card = document.querySelector("[data-partify-fitment-card]");
		return card ? card.getAttribute("data-pfc-state") : null;
	}

	function fitmentPasses() {
		const state = getFitmentState();
		if (state === null || state === "") return false;
		return (
			state !== "fail" &&
			state !== "warn-submodel" &&
			state !== "loading" &&
			state !== "no-vehicle"
		);
	}

	function bodyTypeAcqMismatch() {
		const validator = window.PartifyVinBodyTypeAcq;
		if (validator && typeof validator.hasMismatch === "function") {
			try {
				return validator.hasMismatch() === true;
			} catch (_) {
				return false;
			}
		}
		if (validator && typeof validator.evaluate === "function") {
			try {
				const result = validator.evaluate();
				return !!(result && result.mismatch);
			} catch (_) {
				return false;
			}
		}

		const root = document.getElementById("fitment-questions-root");
		return !!(root && root.getAttribute("data-vin-body-type-mismatch") === "true");
	}

	function focusBodyTypeAcqMismatch() {
		const validator = window.PartifyVinBodyTypeAcq;
		if (validator && typeof validator.focusMismatch === "function") {
			try {
				if (validator.focusMismatch()) return;
			} catch (_) {}
		}
		const root = document.getElementById("fitment-questions-root");
		const group =
			root &&
			root.querySelector(
				'.fitment-type-select[data-vin-body-type-mismatch="true"]',
			);
		if (!group) return;
		try {
			group.scrollIntoView({ behavior: "smooth", block: "center" });
		} catch (_) {
			group.scrollIntoView();
		}
		const checked = group.querySelector("input[type='radio']:not([data-country-origin-choice]):checked");
		if (checked && typeof checked.focus === "function") checked.focus();
	}

	function acqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return [];
		return Array.from(document.querySelectorAll(
			"#fitment-questions-root .fitment-type-select, [data-country-origin-fitment-root] .country-origin-fitment, #fitment-gate-mount .country-origin-fitment",
		));
	}

	function hasUnansweredAcq() {
		const groups = acqGroups();
		for (let i = 0; i < groups.length; i++) {
			if (!groups[i].querySelector("input[type='radio']:not([data-country-origin-choice]):checked")) return true;
		}
		return false;
	}

	// True when there are no ACQ groups, or every group has a checked radio that
	// is not an exclusion. Works whether the ACQ DOM is on the page or in the modal.
	function acqAnswered() {
		const groups = acqGroups();
		if (!groups.length) return true;
		for (let i = 0; i < groups.length; i++) {
			const checked = groups[i].querySelector("input[type='radio']:not([data-country-origin-choice]):checked");
			if (!checked) return false;
			if (checked.getAttribute("data-origin-type") === "exclusion")
				return false;
		}
		return true;
	}

	function hasAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		return !!(
			root &&
			root.querySelector(".fitment-type-select:not(.country-origin-fitment)")
		);
	}

	// Return the checked exclusion radio that makes the part incompatible. Body
	// type mismatches are classified separately to preserve incompatibility precedence.
	function getExclusionAnswer() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return null;
		const checked = document.querySelectorAll(
			"#fitment-questions-root .fitment-type-select input[type='radio']:not([data-country-origin-choice]):checked, [data-country-origin-fitment-root] .country-origin-fitment input[type='radio']:not([data-country-origin-choice]):checked, #fitment-gate-mount .country-origin-fitment input[type='radio']:not([data-country-origin-choice]):checked",
		);
		for (let i = 0; i < checked.length; i++) {
			const group = checked[i].closest(".fitment-type-select");
			if (
				group &&
				group.dataset.bodyTypeAcq === "true" &&
				group.dataset.vinBodyTypeMismatch === "true"
			) {
				continue;
			}
			if (checked[i].getAttribute("data-origin-type") === "exclusion")
				return checked[i];
		}
		return null;
	}

	// A checked exclusion radio anywhere means the part does not fit.
	function hasExclusionAnswer() {
		return !!getExclusionAnswer();
	}

	// The canonical metaobject type and legacy fitment key for the Driver Side /
	// Passenger Side question are both `position`, which the renderer exposes as
	// the exact cart-property name `properties[Position]`. Match that identity,
	// never localized question text or answer copy.
	function hasSideFitmentHardStop() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return false;
		return !!root.querySelector(
			'.fitment-type-select input[type="radio"][name="' +
				SIDE_FITMENT_PROPERTY_NAME +
				'"]:checked[data-origin-type="exclusion"]',
		);
	}

	function selectedOptionAvailable(select) {
		const option = select && select.options[select.selectedIndex];
		return !!(
			select &&
			select.value &&
			option &&
			!option.disabled &&
			option.getAttribute("data-unavailable") !== "true" &&
			option.getAttribute("data-available") !== "false"
		);
	}

	function selectedQualityAvailable() {
		const radios = document.querySelectorAll(
			'input[type="radio"][name="quality_type"]',
		);
		if (!radios.length) return true;

		const checked = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked',
		);
		return !!(
			checked &&
			!checked.disabled &&
			checked.getAttribute("data-available") !== "false" &&
			checked.getAttribute("data-unavailable") !== "true"
		);
	}

	function paintSelectionComplete() {
		const group = document.querySelector(".paint-options-checkbox-group");
		if (!group) return true;
		if (twoToneModeActive()) {
			const helper = window.PartifyTwoTonePaint;
			if (helper && typeof helper.hasExplicitMainSelection === "function") {
				return helper.hasExplicitMainSelection();
			}
			// Two-tone is explicitly manual. If its provenance helper has not loaded,
			// fail closed instead of accepting a stale generic radio/select value.
			return false;
		}

		const checked = group.querySelector(
			'input[name="select_paint_option"]:checked',
		);
		if (!checked) return false;
		if (checked.id !== "checkbox-select-paint-option") return true;

		const select = document.getElementById("variant-selector");
		return !!(select && !select.disabled && selectedOptionAvailable(select));
	}

	function getFormVariantId() {
		const form = document.getElementById("product-form");
		if (!form) return "";

		const idInput = form.querySelector('[name="id"]:not([disabled])');
		if (idInput && idInput.value) return String(idInput.value);

		const match = String(form.getAttribute("action") || "").match(
			/[?&]id=(\d+)/,
		);
		return match ? match[1] : "";
	}

	function hasSelectedAvailableVariant() {
		if (!selectedQualityAvailable() || !paintSelectionComplete()) return false;

		const select = document.getElementById("variant-selector");
		if (select && !select.disabled) return selectedOptionAvailable(select);

		const autoSelectData = document.getElementById("auto-select-variant-data");
		if (autoSelectData && !autoSelectData.getAttribute("data-variant-id"))
			return false;

		const checkedQuality = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked',
		);
		if (checkedQuality && checkedQuality.getAttribute("data-variant-id"))
			return true;

		return !!(getFormVariantId() || window.selectedVariantId);
	}

	function canSubmitWithoutVehicle() {
		const fitmentState = getFitmentState();
		if (
			fitmentState === "fail" ||
			fitmentState === "warn-submodel"
		) return false;
		const countryOrigin = window.PartifyCountryOriginFitment;
		if (
			countryOrigin &&
			typeof countryOrigin.checkerVehiclePassesNativeFitment === "function" &&
			!countryOrigin.checkerVehiclePassesNativeFitment()
		) return false;
		return (
			!getVehicle() &&
			hasAcqGroups() &&
			acqAnswered() &&
			hasSelectedAvailableVariant()
		);
	}

	function twoToneModeActive() {
		return document.documentElement.dataset.partifyTwoTonePaint === "true";
	}

	// Genuine incompatibility (exclusion answer, body-type mismatch, or vehicle
	// fitment failure) — distinct from "a required ACQ hasn't been answered yet."
	// Callers that only care about actual incompatibility (e.g. the FBT "This
	// item" row) should use this instead of isReady()/acqAnswered().
	function isGenuinelyIncompatible() {
		if (window.__PARTIFY_TOUCHUP) return false;
		if (bodyTypeAcqMismatch()) return true;
		if (hasExclusionAnswer()) return true;
		return getFitmentState() === "fail";
	}

	function getReviewReason() {
		if (window.__PARTIFY_TOUCHUP) return "";
		if (bodyTypeAcqMismatch()) return REVIEW_REASON_BODY_TYPE;
		const exclusionAnswer = getExclusionAnswer();
		if (exclusionAnswer) {
			const answer = String(exclusionAnswer.value || "").trim();
			const group = exclusionAnswer.closest(".fitment-type-select");
			const reason =
				group && group.classList.contains("country-origin-fitment")
					? REVIEW_REASON_COUNTRY
					: REVIEW_REASON_EXCLUSION;
			return answer ? reason + ": " + answer : reason;
		}
		if (getFitmentState() === "fail") return REVIEW_REASON_FITMENT;
		return "";
	}

	function reviewPropertySelector(propertyName, activeOnly) {
		return (
			'input[name="properties[' +
			propertyName +
			']"]' +
			(activeOnly ? '[data-partify-fitment-review-active="true"]' : "")
		);
	}

	function restoreReviewPropertyOnForm(form, propertyName) {
		const input = form.querySelector(reviewPropertySelector(propertyName, true));
		if (!input) return;
		if (input.dataset.partifyFitmentReviewCreated === "true") {
			input.remove();
			return;
		}
		input.value = input.dataset.partifyFitmentReviewPreviousValue || "";
		delete input.dataset.partifyFitmentReviewActive;
		delete input.dataset.partifyFitmentReviewPreviousValue;
	}

	function resetReviewAcknowledgement() {
		reviewAcknowledged = false;
		const form = document.getElementById("product-form");
		if (!form) return;
		restoreReviewPropertyOnForm(form, REVIEW_PROPERTY_NAME);
		restoreReviewPropertyOnForm(form, REVIEW_REASON_PROPERTY_NAME);
	}

	function writeManagedReviewProperty(form, propertyName, value) {
		let input = form.querySelector(reviewPropertySelector(propertyName, false));
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = "properties[" + propertyName + "]";
			input.dataset.partifyFitmentReviewCreated = "true";
			form.appendChild(input);
		} else if (input.dataset.partifyFitmentReviewActive !== "true") {
			input.dataset.partifyFitmentReviewPreviousValue = input.value || "";
		}
		input.dataset.partifyFitmentReviewActive = "true";
		input.disabled = false;
		input.value = value;
	}

	function writeReviewPropertyToForm() {
		const form = document.getElementById("product-form");
		if (!form) return;
		writeManagedReviewProperty(
			form,
			REVIEW_PROPERTY_NAME,
			REVIEW_PROPERTY_VALUE,
		);
		const reason = getReviewReason();
		if (reason)
			writeManagedReviewProperty(form, REVIEW_REASON_PROPERTY_NAME, reason);
	}

	// Every readiness condition except the two-tone main-paint choice.
	function baseReadyWithoutPaint() {
		return (
			!bodyTypeAcqMismatch() &&
			!!getVehicle() &&
			fitmentPasses() &&
			acqAnswered()
		);
	}

	function isReady() {
		// Touch-up paint is universal (matched to the vehicle), so it is never fitment-gated.
		if (window.__PARTIFY_TOUCHUP) return true;
		if (!baseReadyWithoutPaint()) return false;
		// Two Colors requires an explicit manual main-product paint choice — vehicle
		// fitment + ACQ answered is not sufficient on its own.
		if (twoToneModeActive() && !paintSelectionComplete()) return false;
		return true;
	}

	// True only when every other readiness condition is satisfied and the sole
	// remaining blocker is an incomplete two-tone main-paint choice.
	function readyButForTwoTonePaint() {
		if (window.__PARTIFY_TOUCHUP) return false;
		return (
			baseReadyWithoutPaint() &&
			twoToneModeActive() &&
			!paintSelectionComplete()
		);
	}

	function requestResolution(continuation) {
		if (modalOpen) return false;
		if (isReady()) return false;
		resolutionContinuation =
			typeof continuation === "function" ? continuation : null;
		openModal();
		if (!modalOpen) resolutionContinuation = null;
		return modalOpen;
	}

	function canSubmitFromModal() {
		if (isReady()) return true;
		if (canAcknowledgeIncompatibility()) return true;
		return canSubmitWithoutVehicle();
	}

	function canAcknowledgeIncompatibility() {
		if (hasSideFitmentHardStop()) return false;
		if (!isGenuinelyIncompatible()) return false;
		if (countryOriginVinIncomplete() || hasUnansweredAcq()) return false;
		const fitmentState = getFitmentState();
		if (
			!fitmentState ||
			fitmentState === "loading" ||
			fitmentState === "warn-submodel"
		) return false;
		if (!paintSelectionComplete() || !hasSelectedAvailableVariant()) return false;
		if (getVehicle()) return fitmentState !== "no-vehicle";
		return hasAcqGroups() && fitmentState !== "fail";
	}

	function canProceed() {
		return isReady() || (reviewAcknowledged && canAcknowledgeIncompatibility());
	}

	function getReviewProperties() {
		if (!reviewAcknowledged || !canAcknowledgeIncompatibility()) return {};
		const properties = { [REVIEW_PROPERTY_NAME]: REVIEW_PROPERTY_VALUE };
		const reason = getReviewReason();
		if (reason) properties[REVIEW_REASON_PROPERTY_NAME] = reason;
		return properties;
	}

	function modalValidationMessage() {
		if (canSubmitFromModal()) return "";
		if (hasSideFitmentHardStop()) {
			return "Please select the correct side before adding this item to cart.";
		}
		if (getFitmentState() === "warn-submodel") {
			return "Please update your vehicle with the required submodel before adding this item to cart.";
		}
		if (countryOriginVinIncomplete()) {
			return "Select your vehicle's manufacturing country, or choose I don't know to check with your VIN or license plate.";
		}
		if (hasUnansweredAcq()) {
			return "Please answer the required fitment questions before adding this item to cart.";
		}
		if (twoToneModeActive() && !paintSelectionComplete()) {
			return "Choose the two-tone paint color for your main part before adding this item to cart.";
		}
		if (!hasSelectedAvailableVariant()) {
			return "Please select an available product option before adding this item to cart.";
		}
		return "Please confirm your fitment before adding this item to cart.";
	}

	/* ── Keep Add to Cart clickable unless a hard requirement is incomplete ── */

	function countryOriginVinIncomplete() {
		const group = document.querySelector(
			".fitment-type-select.country-origin-fitment",
		);
		if (!group) return false;
		const api = window.PartifyCountryOriginFitment;
		if (!api || typeof api.getVerifiedVinRequirement !== "function") return true;
		try {
			const requirement = api.getVerifiedVinRequirement();
			return !requirement || (requirement.applies !== false && !requirement.vin);
		} catch (_) {
			return true;
		}
	}

	// An unfinished country/manufacturing VIN check remains a hard requirement.
	// Genuine incompatibilities open the modal for explicit acknowledgement.
	function shouldBlockAtc() {
		return countryOriginVinIncomplete();
	}

	function isAdding(btn) {
		return (btn.textContent || "").trim() === "Adding…";
	}

	function enableAtcButtons() {
		if (shouldBlockAtc()) {
			document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
				if (!isAdding(btn)) btn.disabled = true;
			});
			return;
		}
		// The base page may have hidden the button via hideAddToCartButton() (e.g.
		// wrong vehicle, no-vehicle, or paint variant not yet selected). Make it
		// visible so the user can click it and trigger the modal.
		if (typeof showAddToCartButton === "function") showAddToCartButton();
		document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
			if (btn.disabled && !isAdding(btn)) btn.disabled = false;
		});
	}

	// The base page re-disables ATC in several places (load, quality/paint
	// transitions, fitment gate). Re-enable whenever it gets disabled while the
	// part still fits and we're not mid-add. Setting disabled=false fires no
	// further action (the handler only acts when disabled===true), so there is no loop.
	function watchAtcButtons() {
		document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
			new FitmentObserver(function () {
				if (isAdding(btn)) return;
				if (shouldBlockAtc()) {
					if (!btn.disabled) btn.disabled = true;
					return;
				}
				if (btn.disabled) {
					if (typeof showAddToCartButton === "function") showAddToCartButton();
					btn.disabled = false;
				}
			}).observe(btn, { attributes: true, attributeFilter: ["disabled"] });
		});
	}

	// Force all ACQ groups in #fitment-questions-root to the enabled state.
	// Mirrors setGroupEnabled(group, true) from product-page-fitment-btns.liquid,
	// which is not globally exposed. Called on init, after auto-select settles,
	// on fitment events, and by watchAcqGroups whenever a group gets re-disabled.
	function enableAllAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return;
		root.querySelectorAll(".fitment-type-select").forEach(function (group) {
			group.classList.remove("fitment-disabled");
			group.style.pointerEvents = "auto";

			const inner = group.querySelector(".fitment-options-container");
			if (inner) {
				inner.classList.remove("fitment-disabled");
				inner.style.pointerEvents = "auto";
			}

			group.querySelectorAll("input[type='radio']").forEach(function (r) {
				r.disabled = false;
				r.classList.remove("fitment-disabled");
				r.classList.remove("individual-fitment-select-disabled");
			});

			group.querySelectorAll(".fitmentText").forEach(function (l) {
				l.style.color = "#1D1D1D";
				l.classList.remove("fitment-disabled");
				l.classList.remove("individual-fitment-label-disabled");
			});

			group.querySelectorAll(".option-title-fitment").forEach(function (t) {
				t.style.color = "#e61b24";
			});

			const modalBtn = group.querySelector(".fitment-button-description");
			if (modalBtn) {
				modalBtn.disabled = false;
				modalBtn.style.opacity = "1";
			}
		});
	}

	// Watch #fitment-questions-root for any runtime disabling (class or disabled
	// attribute changes) and immediately re-enable. The debounce timer ensures
	// the loop naturally terminates once all groups are already enabled.
	function watchAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return;
		let timer = null;
		new FitmentObserver(function () {
			clearTimeout(timer);
			timer = delay(enableAllAcqGroups, 0);
		}).observe(root, {
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "disabled"],
		});
	}

	// Clear the initial "greyed / step-locked" render state that the base page
	// only clears on fitment success: in-stock badges and the help/modal buttons
	// (e.g. "Not sure which quality?"). The disable* functions are no-ops while
	// the gate is on, so nothing re-greys them.
	function normalizeCustomizationUI() {
		document
			.querySelectorAll(".quality-in-stock-badge.disabled")
			.forEach(function (el) {
				el.classList.remove("disabled");
			});
		if (typeof enableQualityDescriptionBtn === "function")
			enableQualityDescriptionBtn();
		// Enable paint-code help button directly — calling enablehowToFindPaintCodeBtn() triggers
		// enablePaintOptionRadioBtns() → initPaintOptionDefaults() → dispatches a bubbling
		// 'change' on the unpainted radio → the page's paint handlers loop infinitely → stack overflow.
		const paintCodeHelpBtn = document.getElementById(
			"how-to-find-your-paint-code-vindecoder",
		);
		if (paintCodeHelpBtn) paintCodeHelpBtn.disabled = false;
		if (typeof enableGetPaintCodeUsingVin === "function")
			enableGetPaintCodeUsingVin();
		if (typeof enableSelectVinVariantBtn === "function")
			enableSelectVinVariantBtn();
	}

	/* ── Modal move in / out ── */

	function fitmentNodes() {
		// Move BOTH the fitment card and the ACQ container (which wraps
		// #fitment-questions-root). Filter out anything missing.
		const nodes = [];
		const card = document.querySelector("[data-partify-fitment-card]");
		const country = document.querySelector("[data-country-origin-fitment-root] .country-origin-fitment");
		const acq = document.querySelector(".required-questions-container");
		if (card) nodes.push(card);
		if (country) nodes.push(country);
		if (acq) nodes.push(acq);
		return nodes;
	}

	function openModal() {
		const modal = document.getElementById("fitment-gate-modal");
		const mount = document.getElementById("fitment-gate-mount");
		if (!modal || !mount || modalOpen) return;
		resetReviewAcknowledgement();
		lastFocusedElement = document.activeElement;

		movedNodes = [];
		fitmentNodes().forEach(function (node) {
			movedNodes.push({
				node: node,
				parent: node.parentNode,
				nextSibling: node.nextSibling,
			});
			mount.appendChild(node);
		});

		modal.style.display = "flex";
		modalOpen = true;
		syncModalAtc();
		const title = document.getElementById("fitment-gate-title");
		if (title && typeof title.focus === "function") title.focus();
		LOG("modal opened; moved", movedNodes.length, "node(s)");
	}

	function restoreNodes() {
		// Put the moved fitment DOM back exactly where it came from.
		movedNodes.forEach(function (entry) {
			if (entry.nextSibling && entry.nextSibling.parentNode === entry.parent) {
				entry.parent.insertBefore(entry.node, entry.nextSibling);
			} else {
				entry.parent.appendChild(entry.node);
			}
		});
		movedNodes = [];
	}

	function closeModal() {
		const modal = document.getElementById("fitment-gate-modal");
		resolutionContinuation = null;
		if (!modal || !modalOpen) return;
		restoreNodes();
		modal.style.display = "none";
		modalOpen = false;
		if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
			lastFocusedElement.focus();
		}
		lastFocusedElement = null;
		LOG("modal closed; fitment restored to page");
	}

	// External flows (e.g. answering Two Colors from inside the modal) need to pull
	// the shopper out of the deferred-fitment modal without resuming Add to Cart.
	// Reuses closeModal() as-is; returns whether the modal was actually open.
	function dismissForExternalFlow() {
		const wasOpen = modalOpen;
		closeModal();
		return wasOpen;
	}

	/* ── Modal Add to Cart state ── */

	function syncModalAtc() {
		const btn = document.querySelector(".fgm-add-to-cart");
		const message = document.querySelector(".fgm-validation-message");
		const warning = document.querySelector(".fgm-fitment-warning");
		const title = document.getElementById("fitment-gate-title");
		const incompatible = isGenuinelyIncompatible();
		const sideHardStop = hasSideFitmentHardStop();
		const ready = canSubmitFromModal();
		if (btn) {
			btn.disabled = !ready;
			btn.textContent = sideHardStop
				? "Add to Cart"
				: incompatible
					? "Continue Anyway"
					: "Add to Cart";
		}
		if (title) title.textContent = incompatible ? "Fitment Warning" : "Confirm Your Fitment";
		if (warning) warning.hidden = !incompatible || sideHardStop;
		if (message) {
			message.textContent = ready ? "" : modalValidationMessage();
			message.hidden = ready;
		}
	}

	function confirmFromModal() {
		if (!canSubmitFromModal()) return;
		if (isGenuinelyIncompatible()) {
			reviewAcknowledged = true;
			writeReviewPropertyToForm();
		} else {
			resetReviewAcknowledgement();
		}
		const continuation = resolutionContinuation;
		resolutionContinuation = null;
		restoreNodes();
		const modal = document.getElementById("fitment-gate-modal");
		if (modal) modal.style.display = "none";
		modalOpen = false;
		lastFocusedElement = null;
		if (continuation) {
			Promise.resolve()
				.then(function () {
					return continuation();
				})
				.catch(function (error) {
					console.error("wool [pdp-fitment-gate] continuation failed", error);
				});
			return;
		}
		LOG("modal confirm → submitting product form");
		const form = document.getElementById("product-form");
		if (!form) {
			LOG("ERROR: #product-form not found!");
			return;
		}

		// requestSubmit() bypasses the ATC click handler that normally writes the
		// selected variant into input[name="id"]. For legacy quality products the
		// quality change handler only fires on user-triggered `change` events — an
		// auto-selected radio on page load never fires one, so window.selectedVariantId
		// stays at the base product's first variant. Read data-variant-id from the
		// checked quality radio directly as the source of truth.
		// For all other products (no quality radios) the Liquid template already wrote
		// the correct variant ID into the form — leave it untouched.
		const checkedQuality = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked',
		);
		const qualityVariantId =
			checkedQuality && checkedQuality.getAttribute("data-variant-id");
		if (qualityVariantId) {
			const idInput = form.querySelector('input[name="id"]');
			if (idInput && idInput.value !== String(qualityVariantId))
				idInput.value = qualityVariantId;
		}

		window.__pdpFitmentGateConfirming = true;
		const selectedAssembly = document.querySelector(
			'input[name="bundle_mode"][value="kit"]:checked',
		);
		const addButton = form.querySelector(
			".add-to-cart, .add-to-cart-for-unpainted",
		);
		if (selectedAssembly && addButton) {
			// Assembly is click-driven and adds multiple lines directly. Resume that
			// path now that fitment is confirmed instead of submitting only the part.
			addButton.disabled = false;
			addButton.click();
		} else if (typeof form.requestSubmit === "function") {
			form.requestSubmit();
		} else {
			if (addButton) {
				addButton.disabled = false;
				addButton.click();
			}
		}
		delay(function () {
			window.__pdpFitmentGateConfirming = false;
		}, 0);
	}

	/* ── Submit gate (capture phase, runs before the page's submit handler) ── */

	function onProductFormSubmitCapture(e) {
		const form = e.target;
		if (!form || form.getAttribute("id") !== "product-form") return;
		if (modalOpen) {
			if (!hasSideFitmentHardStop()) return;
			e.preventDefault();
			e.stopImmediatePropagation();
			syncModalAtc();
			return;
		}
		if (
			window.__pdpFitmentGateConfirming &&
			(canProceed() || canSubmitWithoutVehicle())
		) return;
		if (isReady()) return;
		e.preventDefault();
		e.stopImmediatePropagation();
		openModal();
	}

	function onAddToCartClickCapture(e) {
		const button = e.target && e.target.closest
			? e.target.closest(ATC_BTN_SELECTOR)
			: null;
		if (
			!button ||
			window.__pdpFitmentGateConfirming ||
			!isGenuinelyIncompatible()
		) return;
		e.preventDefault();
		e.stopImmediatePropagation();
		openModal();
	}

	/* ── Wire up ── */

	function init() {
		enableAtcButtons();
		normalizeCustomizationUI();
		enableAllAcqGroups();
		watchAtcButtons();
		watchAcqGroups();
		// Auto-select (partify-cro.js) settles asynchronously; re-assert after it.
		delay(function () {
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
		}, 700);

		listen(document, "click", onAddToCartClickCapture, true);
		listen(document, "submit", onProductFormSubmitCapture, true);

		// Modal dismiss (backdrop + close button).
		const modal = document.getElementById("fitment-gate-modal");
		if (modal) {
			modal.addEventListener("click", function (e) {
				if (e.target.closest("[data-fgm-dismiss]")) {
					e.preventDefault();
					closeModal();
				}
			});
		}
		listen(document, "keydown", function (e) {
			if (modalOpen && e.key === "Escape") { closeModal(); options.onDismiss?.(); }
		});

		// Modal Add to Cart.
		const fgmBtn = document.querySelector(".fgm-add-to-cart");
		if (fgmBtn) fgmBtn.addEventListener("click", confirmFromModal);

		// Re-assert ATC state + modal button on fitment changes.
		listen(document, "change", function (e) {
			if (!e.target || e.target.type !== "radio") return;
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			if (modalOpen) syncModalAtc();
		});
		listen(document, "partify:country-origin-change", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			if (modalOpen) syncModalAtc();
		});
		listen(document, "partify:vin-body-type-acq-evaluated", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			if (modalOpen) syncModalAtc();
		});
		listen(window, "partify:native-fitment-resolved", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
			if (modalOpen) delay(syncModalAtc, 50);
		});
		function onVehicleChanged() {
			resetReviewAcknowledgement();
			enableAtcButtons();
			if (modalOpen) syncModalAtc();
		}
		listen(window, "searchTermsUpdated", onVehicleChanged);
		listen(window, "partify:vehicle:changed", onVehicleChanged);
		listen(window, "storage", function (e) {
			if (e.key === "searchTerms") onVehicleChanged();
		});
		listen(window, "pageshow", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
			if (modalOpen) syncModalAtc();
		});
		const card = document.querySelector("[data-partify-fitment-card]");
		if (card) {
			new FitmentObserver(function () {
				resetReviewAcknowledgement();
				enableAtcButtons();
				normalizeCustomizationUI();
				if (
					window.__partifyCountryOriginRestorePending &&
					window.PartifyCountryOriginFitment &&
					typeof window.PartifyCountryOriginFitment.restorePending === "function"
				) window.PartifyCountryOriginFitment.restorePending();
				if (modalOpen) syncModalAtc();
			}).observe(card, {
				attributes: true,
				attributeFilter: ["data-pfc-state"],
			});
		}

		LOG("initialized");
	}

	window.__pdpFitmentGateIsReady = isReady;
	window.__pdpFitmentGateCanProceed = canProceed;
	window.__pdpFitmentGateRequestResolution = requestResolution;
	window.__pdpFitmentGateIsIncompatible = isGenuinelyIncompatible;
	window.__pdpFitmentGateGetReviewProperties = getReviewProperties;
	window.__pdpFitmentGateHasUnansweredAcq = hasUnansweredAcq;
	window.__pdpFitmentGateHasSideHardStop = hasSideFitmentHardStop;
	window.__pdpFitmentGateDismiss = dismissForExternalFlow;
	window.__pdpFitmentGateReadyButForPaint = readyButForTwoTonePaint;

    init();
    activeInstance = { destroy() { lifecycle.destroy(); restoreNodes(); const modal = document.getElementById('fitment-gate-modal'); if (modal) modal.style.display = 'none'; modalOpen = false; } };
    return activeInstance;
  }
  window.PartifyPdpFitmentGate = { mount, createLifecycle };
  function boot() { if (window.__pdpFitmentGate) mount(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
